// Ctrl-K over everything already loaded.
//
// The index is rebuilt only when the data behind it changes, not on every
// keystroke: typing must stay typing (the project's own rule), and an index of a
// thousand spells rebuilt per character is exactly how a palette comes to feel
// slow at the one moment the DM is impatient.
//
// Ranking lives in state/search.ts and is tested there; what lives here is the
// keyboard: up and down to move, Enter to take the row, Escape to leave.
import { useEffect, useMemo, useRef, useState } from "react";
import { useFocusTrap } from "./FocusTrap";
import { useStore } from "../state/store";
import reference from "../content/reference.json";
import { group, highlight, labelFor, rank, type Hit } from "../state/search";
import { actionItems, buildIndex, conditionItems, masteryItems, pageItems, partyItems, rulingItems, spellItems, trackerItems, type Reference } from "../state/searchIndex";

export function SearchPalette({ onClose, onStatBlock }: { onClose: () => void; onStatBlock?: (name: string) => void }) {
  const { layout, update } = useStore();
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const box = useRef<HTMLInputElement>(null);
  /* Tab stays in the palette; Escape leaves it; focus goes back to whatever the
     DM was on when they pressed Ctrl-K. */
  const trap = useFocusTrap<HTMLDivElement>(onClose);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => { box.current?.focus(); }, []);

  /* Rebuilt when the things it indexes change, which at a table is rarely --
     a creature joining the fight, a character's spells arriving. Never per
     keystroke. */
  const index = useMemo(() => buildIndex([
    trackerItems(layout),
    partyItems(layout),
    pageItems(),
    conditionItems(reference as Reference),
    rulingItems(reference as Reference),
    actionItems(reference as Reference),
    masteryItems(reference as Reference),
    spellItems(layout),
  ]), [layout.externalInitiative, layout.characters, layout.activeCampaign, layout.ddbSpellCatalogue]);

  const hits = useMemo(() => rank(index, q), [index, q]);
  const groups = useMemo(() => group(hits), [hits]);
  /* One flat list for the keyboard, in the order the groups draw them, so Down
     moves to what is visually next and not to what happens to be next in a
     different array. */
  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);
  useEffect(() => { setAt(0); }, [q]);

  const take = (hit: Hit | undefined) => {
    if (!hit) return;
    /* A creature's stat block is the one action worth taking straight from a
       search result: it is reading, it is reversible, and it is the question
       most often asked about a name. */
    if (hit.action?.statBlock && onStatBlock) {
      if (hit.action.nav) update((l) => ({ ...l, activeNav: hit.action!.nav! }));
      onStatBlock(hit.action.statBlock);
      onClose();
      return;
    }
    if (hit.action?.nav) { update((l) => ({ ...l, activeNav: hit.action!.nav! })); onClose(); return; }
    /* Nothing to do with it: the row has already shown what it says, which for a
       condition or a ruling is the whole point of looking. */
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAt((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); take(flat[at]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  /* Keep the chosen row in view when the keyboard moves past the fold. */
  useEffect(() => {
    const el = list.current?.querySelector(`[data-hit="${at}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: "nearest" });
  }, [at]);

  return (
    <div className="palette-scrim" onClick={onClose} role="presentation">
      <div ref={trap} className="palette" role="dialog" aria-label="Search everything" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="palette-box">
          <span aria-hidden="true">&#x2315;</span>
          <input ref={box} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Spells, conditions, rulings, creatures, the party, a page&hellip;"
            aria-label="Search everything" aria-controls="palette-results" autoComplete="off" spellCheck={false} />
          <kbd>Esc</kbd>
        </div>

        <div className="palette-results" id="palette-results" ref={list} role="listbox" aria-label="Results">
          {!q && (
            <p className="muted small palette-hint">
              {index.length} things to search. Up and down to move, Enter to take a result,
              Esc to leave. A creature on the tracker opens its stat block.
            </p>
          )}
          {Boolean(q) && !flat.length && <p className="muted small palette-hint">Nothing matches &ldquo;{q}&rdquo;.</p>}
          {groups.map((g) => (
            <div key={g.kind} className="palette-group">
              <h5>{labelFor(g.kind)}</h5>
              {g.hits.map((h) => {
                const i = flat.indexOf(h);
                const parts = highlight(h.name, q);
                return (
                  <button key={h.id} type="button" data-hit={i} role="option" aria-selected={i === at}
                    className={`palette-row ${i === at ? "at" : ""}`}
                    onMouseEnter={() => setAt(i)} onClick={() => take(h)}>
                    <span className="palette-name">
                      {parts ? <>{parts[0]}<mark>{parts[1]}</mark>{parts[2]}</> : h.name}
                    </span>
                    {Boolean(h.detail) && <small className="palette-detail">{h.detail}</small>}
                    {/* WHY a row is here, when the reason is not visible in it.
                        A hit whose cause the DM cannot see reads as a wrong answer. */}
                    {h.why === "body" && <em className="palette-why">mentions it</em>}
                    {h.action && <em className="palette-verb">{h.action.verb}</em>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
